"use client";
import { useState, useEffect, useMemo } from "react";
import st from "@/styles/modules/typography.module.scss";
import s from "./TitlePageGenerator.module.scss";
import { Calendar } from "components/UI/form/Calendar";
import { Button } from "components/UI/buttons/Button";
import { Checkbox } from "components/UI/form/Checkbox";
import { Link } from "components/shared/Link";
import { APAPreview } from "./APAPreview";
import { MLAPreview } from "./MLAPreview";
import { ChicagoPreview } from "./ChicagoPreview";
import { fieldsConfig } from "./fieldsConfig";
import { Input } from "components/UI/form/Input";
import { Portal } from "components/shared/Portal";
import { Modal } from "components/shared/Modal";
import { Textarea } from "components/UI/form/Textarea";
import cn from "classnames";
import { TabButtons } from "@/components/shared/TabButtons";
import { SectionToolsCTABlock } from "@/components/pages/tool-components/shared/SectionToolsCTABlock";

type StateType = {
  title: string;
  institution: string;
  instructor: string;
  course: string;
  runningHead: string;
  firstName: string;
  lastName: string;
  abstract: string;
  date: null | Date;
};

const DOC_FORMAT = "docx";
const DOC_TITLE = "titlepage";
const DOMAIN = "https://essay-toolbox.sky-services.cc/";

export const TitlePageGenerator = () => {
  const [typeId, setTypeId] = useState<string | number>(1);
  const [preview, setPreview] = useState(false);
  const [data, setData] = useState<StateType>({
    title: "",
    institution: "",
    instructor: "",
    course: "",
    runningHead: "",
    abstract: "",
    firstName: "",
    lastName: "",
    date: null,
  });

  const [allFilled, setAllFilled] = useState(false);

  const fetchMethod = (
    { title, institution, instructor, course, runningHead, firstName, lastName, date, abstract }: StateType,
    typeId: number
  ) => {
    if (!date) return;
    const paperFormat = (typeId === 1 && "apa") || (typeId === 2 && "mla") || (typeId === 3 && "chicago");

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    const API_URL = `${DOMAIN}api/page_generator/documents/${paperFormat}/create/${DOC_FORMAT}`;

    const filename = `${paperFormat}_${DOC_TITLE}.${DOC_FORMAT}`;
    const fetchBody = {
      month,
      day,
      year,
      professorFirstName: instructor.trim(),
      course: course.trim(),
      firstName: firstName.trim(),
      institution: institution.trim(),
      lastName: lastName.trim(),
      runningHead: runningHead.trim(),
      title: title.trim(),
      abstract: abstract.trim(),
    };

    fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
        "Content-Disposition": 'attachment; filename="' + filename + '"',
      },
      body: JSON.stringify(fetchBody),
    })
      .then((response) => {
        if (response.status !== 200) console.log("BAD RESPONSE");
        return response.blob();
      })
      .then((data) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(data);
        link.setAttribute("download", filename);
        link.click();
      })
      .catch((error) => console.error(`Fetch Error [${error.name}]: ${error.message}`));
  };

  const activeFieldSet = useMemo(() => {
    return fieldsConfig.find((type) => type.id === typeId)?.fields;
  }, [typeId]);

  useEffect(() => {
    if (!activeFieldSet) {
      setAllFilled(false);
      return;
    }

    for (const field of activeFieldSet) {
      if (!data[field.name]) {
        setAllFilled(false);
        return;
      }
    }
    setAllFilled(true);
  }, [data, activeFieldSet]);

  const handleInput = (event: React.ChangeEvent) => {
    const target = event.target as HTMLInputElement;
    let key = target.name;

    if (key) setData({ ...data, [key]: target.value.trimStart() });
  };

  const onDateChange = (date: Date | [Date | null, Date | null] | null) => {
    if (Array.isArray(date)) return;
    setData({ ...data, date });
  };

  return (
    <div className={cn(st.card, s.tool)}>
      <div className={s.inner}>
        <div className={s.previewWrap}>
          {typeId === 1 && <APAPreview />}
          {typeId === 2 && <MLAPreview />}
          {typeId === 3 && <ChicagoPreview />}
        </div>

        <Portal>
          <div className={s.previewWrapMob}>
            <Modal open={preview} setOpen={setPreview} type="formatPreview">
              {typeId === 1 && <APAPreview />}
              {typeId === 2 && <MLAPreview />}
              {typeId === 3 && <ChicagoPreview />}
            </Modal>
          </div>
        </Portal>

        <div className={s.form}>
          <div className={s.top}>
            <TabButtons
              items={fieldsConfig.map(({ id, name }) => ({
                tabKey: id,
                button: name,
              }))}
              activeKey={typeId}
              onChange={setTypeId}
              breakpoint="none"
              className={s.typeBtns}
            />

            <Button tag="button" type="secondary" onClick={() => setPreview(true)} className={s.previewBtn}>
              Preview example
            </Button>
          </div>
          <div className={s.fields}>
            {activeFieldSet &&
              activeFieldSet.map((field) =>
                field.type === "calendar" ? (
                  <Calendar
                    key={field.name}
                    onChange={onDateChange}
                    startDate={data[field.name] as Date}
                    endDate={null}
                    label={field.placeholder}
                  />
                ) : field.type === "textarea" ? (
                  <Textarea
                    key={field.name}
                    name={field.name}
                    initialValue={"" + data[field.name]}
                    label={field.placeholder}
                    onChange={handleInput}
                    maxLength={field.maxLength}
                    className={s.textarea}
                  />
                ) : (
                  <Input
                    key={field.name}
                    name={field.name}
                    initialValue={"" + data[field.name]}
                    label={field.placeholder}
                    onChange={handleInput}
                    maxLength={field.maxLength}
                  />
                )
              )}
            <div className={s.btnWrap}>
              <Button
                tag="button"
                onClick={() => fetchMethod(data, Number(typeId))}
                disabled={!allFilled}
                type="secondary"
                className={s.generate}
              >
                Generate
              </Button>
            </div>
          </div>
        </div>
      </div>

      <SectionToolsCTABlock />
    </div>
  );
};
